// ============================================================
// Ce que le PLANNING met sur le papier — module PUR, aucune dépendance Angular.
// Testé sans TestBed : hr-planning-print.spec.ts.
// ============================================================
//
// Trois documents sortent de l'écran de planning, et ce sont trois documents DIFFÉRENTS :
//   · le PLANNING de la semaine    — ce qui est affiché, tel qu'il est affiché ;
//   · la FEUILLE D'HEURES à signer — toujours par salarié, même si l'écran est en vue Poste :
//     on ne fait pas signer une colonne « Magasin », on fait signer une personne ;
//   · les HORAIRES TYPES           — les grilles de roulement, pas une semaine réelle. C'est le
//     document du mur d'atelier et de l'embauche.
//
// POURQUOI CE FICHIER EXISTE. Ces 300 lignes vivaient dans le composant, au milieu du glisser-
// déposer et des appels serveur. Elles n'y étaient pas testables : rien de ce qui décide de ce qui
// part à l'imprimante — quelles colonnes, quels totaux, quelles pastilles — ne pouvait être vérifié
// sans monter un TestBed et un navigateur. Ici, tout est une fonction de ses arguments.
//
// LE CONTRAT D'ENTRÉE est structurel, jamais les types du composant : ce module déclare exactement
// ce qu'il lit, et rien de plus. Les libellés que lui seul ne saurait pas produire — la couleur
// d'une personne, le nom d'un lieu, les pastilles d'absence — arrivent en RÉSOLVEURS. C'est ce qui
// le garde pur tout en lui laissant dire « ce bloc est de cette couleur-là ».

import { EffectiveSlot } from '../hr-schedule-engine';
import { HrSchedule, hrEmployeeNameOf, hrOpeningOf, hrScheduleActivityId } from '@app/shared/_models/hr';
import { HrActivity, HrEmployee } from '@app/shared/_models/hr';
import { PrintDay, PrintDoc, PrintRow, PrintSection } from '../hr-print';
import { SchedulePrintGrid, buildSchedulePrintDoc } from '../hr-schedule-print';
import { comparePeriods, hoursLabel, minutesLabel, plannedMinutes, schedulePeriodStatus } from '../hr-schedule-engine';
import { WorkTimeRules, effectiveWorkRules, employeeWorkProfileOf } from '../hr-worktime';
import { rosterOrderOf } from '../hr-roster';

// ============================================================
// Ce que le module LIT de l'écran
// ============================================================

/// Un jour de la semaine affichée, réduit à ce que le papier en montre.
export interface PrintWeekDay {
	dat: string;
	label: string;
	hours: string;					// amplitude d'ouverture « 09:00 – 19:00 » (vide si fermé)
	holiday: string | null;
	open: boolean;
	note: string;
}

export interface PrintAbsenceBadge { label: string; pending: boolean; }

/// Ligne de la vue SALARIÉ : la personne, et ses créneaux jour par jour.
export interface PrintEmployeeRow {
	id_employee: number;
	name: string;
	total_min: number;
	delta_min: number | null;
	counts_hours: boolean;
	cells: { dat: string; slots: EffectiveSlot[]; absence: PrintAbsenceBadge | null }[];
}

/// Ligne de la vue POSTE : le service, et qui le tient jour par jour.
export interface PrintPosteRow {
	name: string;
	color: string;
	total_min: number;
	cells: { dat: string; entries: { slot: EffectiveSlot; name: string }[]; people: number; minutes: number }[];
}

/// Les libellés que le module ne peut pas déduire seul — ils dépendent des référentiels chargés
/// par l'écran (magasins, services, fiches, couleurs choisies à la main).
export interface PrintResolvers {
	employeeColor(id_employee: number): string;
	slotColor(s: EffectiveSlot): string;
	placeLabel(s: EffectiveSlot): string;
	/// Pastilles qui EXPLIQUENT le total (« 3 j CP ») : sans elles, une semaine à 16 h sur un
	/// contrat de 35 h passe pour une erreur de calcul.
	absenceChips(row: PrintEmployeeRow): string[];
	/// L'absence couvre-t-elle la journée ENTIÈRE ? Une demi-journée laisse ses créneaux visibles.
	fullDayAbsence(cell: { slots: EffectiveSlot[]; absence: PrintAbsenceBadge | null }): PrintAbsenceBadge | null;
	/// Pied de grille de la vue Poste : l'écran le calcule déjà, on ne le refait pas.
	posteDayTotals(dayIdx: number): { people: number; minutes: number };
}

export interface PlanningPrintInput {
	kind: 'planning' | 'timesheet';
	byPoste: boolean;					// vue affichée ; la feuille d'heures force le mode salarié
	dayIdx: number[];					// indices des jours VISIBLES (les jours fermés masqués n'ont pas de colonne)
	weekDays: PrintWeekDay[];			// les 7, indexés comme dayIdx
	weekLabel: string;
	storeName: string;
	printedAt: string;
	employeeRows: PrintEmployeeRow[];
	posteRows: PrintPosteRow[];
	resolvers: PrintResolvers;
}

// ============================================================
// Planning de la semaine / feuille d'heures
// ============================================================

/// Assemble le document à partir de CE QUI EST AFFICHÉ — mêmes lignes, mêmes jours, mêmes totaux.
///
/// Il partait autrefois des SECTIONS (une grille par emploi du temps), structure que l'écran
/// n'utilise plus depuis les vues Salarié/Poste : un salarié membre de quatre grilles sortait
/// QUATRE FOIS sur le papier, dont trois lignes vides. On imprime donc les lignes affichées, et
/// seulement les jours affichés.
export function buildPlanningPrintDoc(input: PlanningPrintInput): PrintDoc {
	const timesheet = input.kind === 'timesheet';
	// La feuille d'heures se signe PAR PERSONNE : toujours en vue Salarié, même si l'écran est en
	// vue Poste. Le planning, lui, imprime la vue sous les yeux.
	const byPoste = !timesheet && input.byPoste;

	const days: PrintDay[] = input.dayIdx.map(i => ({
		dat: input.weekDays[i].dat,
		label: input.weekDays[i].label,
		hours: input.weekDays[i].hours,
		holiday: input.weekDays[i].holiday,
		closed: !input.weekDays[i].open,
	}));

	const rows = byPoste ? postePrintRows(input) : employeePrintRows(input);

	const section: PrintSection = {
		title: '',
		subtitle: '',
		caption: (timesheet ? 'Feuille d\'heures' : 'Planning') + ' — ' + input.storeName + ' — ' + input.weekLabel,
		rowHeader: byPoste ? 'Poste' : 'Salarié',
		totalHeader: timesheet ? 'Prévu' : 'Total',
		footHeader: 'Présence',
		footTotal: hoursLabel(rows.reduce((sum, r) => sum + (r.counts_hours ? r.total_min : 0), 0)),
		days,
		rows,
		foot: input.dayIdx.map(i => {
			const t = printDayTotals(input, i, byPoste);
			return { people: t.people + ' pers.', hours: hoursLabel(t.minutes), closed: !input.weekDays[i].open };
		}),
		timesheet,
		pageBreak: false,
	};

	return {
		kind: input.kind,
		heading: timesheet ? 'Feuille d\'heures' : 'Planning de la semaine',
		storeName: input.storeName,
		subtitle: byPoste ? 'vue par poste' : 'vue par salarié',
		periodLabel: input.weekLabel,
		printedAt: input.printedAt,
		legend: timesheet
			? 'Chaque salarié complète ses heures réellement effectuées, indique l\'écart éventuel et signe. '
				+ 'La feuille signée fait foi pour la mise à jour du planning.'
			: '',
		sections: [section],
		ledgers: [],
		// Les notes de journée quittent la grille : intercalées dans le tableau, elles cassaient
		// l'alignement des colonnes et pouvaient se retrouver seules en haut d'une page.
		notes: input.dayIdx
			.filter(i => !!input.weekDays[i].note)
			.map(i => ({ day: input.weekDays[i].label, txt: input.weekDays[i].note })),
		rotation: null,
		signature: timesheet,
	};
}

/// Vue Salarié : une ligne par personne, tous services et magasins confondus (comme à l'écran).
function employeePrintRows(input: PlanningPrintInput): PrintRow[] {
	const r = input.resolvers;
	// Le PLANNING (kind 'planning') est fait pour l'affichage COLLECTIF — la salle de repos : y
	// écrire « Arrêt maladie » à côté d'un nom divulgue une donnée de santé à toute l'équipe
	// (art. 9 RGPD ; CNIL, référentiel gestion du personnel). Le motif y est donc neutralisé en
	// « Absent ». La feuille d'heures (kind 'timesheet'), remise à chaque salarié et archivée par
	// le responsable RH, reste un document interne : elle garde le motif — comme le relevé
	// individuel (kind 'ledger').
	const neutral = input.kind === 'planning';
	const label = (b: PrintAbsenceBadge | null): string | null =>
		!b ? null : neutral ? 'Absent' + (b.pending ? ' (demandé)' : '') : b.label;
	return input.employeeRows.map(row => ({
		name: row.name,
		subtitle: '',
		color: r.employeeColor(row.id_employee),
		chips: r.absenceChips(row),
		total_min: row.total_min,
		delta_min: row.counts_hours === false ? null : row.delta_min,
		counts_hours: row.counts_hours !== false,
		cells: input.dayIdx.map(i => {
			const cell = row.cells[i];
			const full = r.fullDayAbsence(cell);
			return {
				dat: cell.dat,
				absence: full ? label(full) : label(cell.absence),
				pending: !!cell.absence?.pending,
				sub: '',
				holiday: !!input.weekDays[i].holiday,
				closed: !input.weekDays[i].open,
				blocks: full ? [] : cell.slots.filter(s => !s.absent).map(s => ({
					who: '',
					place: r.placeLabel(s),
					from: minutesLabel(s.start_min),
					to: minutesLabel(s.end_min),
					break_min: s.break_min ?? 0,
					// Même code couleur qu'à l'écran : le papier ne doit pas raconter autre chose.
					color: r.slotColor(s),
				})),
			};
		}),
	}));
}

/// Vue Poste : une ligne par service, les personnes dans les cases — « qui tient ce poste ».
function postePrintRows(input: PlanningPrintInput): PrintRow[] {
	const r = input.resolvers;
	return input.posteRows.map(row => ({
		name: row.name,
		subtitle: '',
		color: row.color,
		chips: [],
		total_min: row.total_min,
		delta_min: null,
		counts_hours: true,
		cells: input.dayIdx.map(i => {
			const cell = row.cells[i];
			return {
				dat: cell.dat,
				absence: null,
				pending: false,
				sub: cell.entries.length ? cell.people + ' pers. · ' + hoursLabel(cell.minutes) : '',
				holiday: !!input.weekDays[i].holiday,
				closed: !input.weekDays[i].open,
				blocks: cell.entries.map(e => ({
					who: e.name,
					place: '',
					from: minutesLabel(e.slot.start_min),
					to: minutesLabel(e.slot.end_min),
					break_min: e.slot.break_min ?? 0,
					color: r.employeeColor(e.slot.id_employee),
				})),
			};
		}),
	}));
}

/// Pied de grille imprimé : ce que l'écran affiche déjà sous la vue Postes, étendu à la vue
/// Salarié (« combien de monde ce lundi ? » se pose dans les deux sens de lecture).
function printDayTotals(input: PlanningPrintInput, dayIdx: number, byPoste: boolean): { people: number; minutes: number } {
	if (byPoste) return input.resolvers.posteDayTotals(dayIdx);
	const slots = input.employeeRows.flatMap(row => (row.cells[dayIdx]?.slots ?? []).filter(s => !s.absent));
	return { people: new Set(slots.map(s => s.id_employee)).size, minutes: plannedMinutes(slots) };
}

// ============================================================
// Horaires types — les grilles de roulement
// ============================================================

export interface SchedulesPrintInput {
	schedules: HrSchedule[];
	activities: HrActivity[];
	employees: HrEmployee[];
	users: { id?: number; firstname?: string; lastname?: string }[];
	rules: WorkTimeRules;
	/// Magasin sélectionné, ou null pour toute l'organisation.
	store: number | null;
	storeName: string;
	/// id de magasin → nom. Le module ne connaît pas c_store : l'écran, si.
	storeNames: Map<number, string>;
	printedAt: string;
	today: string;
	employeeColor(id_employee: number): string;
	/// Fusion des plages d'ouverture d'un jour (l'écran a déjà cette fonction, on ne la duplique pas).
	mergeRanges(ranges: { start_min: number; end_min: number }[]): { start_min: number; end_min: number }[];
}

/// Les grilles ACTIVES qui SERVENT ENCORE — en cours et à venir. Les archives (des centaines au
/// fil des années) n'ont rien à faire au mur. `null` quand il n'y a rien à imprimer.
export function buildSchedulesPrintDoc(input: SchedulesPrintInput): PrintDoc | null {
	const grids = input.schedules
		.filter(s => (input.store == null || s.id_store === input.store) && s.is_active !== false)
		.filter(s => schedulePeriodStatus(s, input.today) !== 'past')
		.sort((a, b) => storeLabelOf(input, a.id_store).localeCompare(storeLabelOf(input, b.id_store))
			|| comparePeriods(a, b))
		.map(sch => printGridOf(input, sch));

	if (grids.length === 0) return null;
	return buildSchedulePrintDoc({
		storeName: input.storeName,
		printedAt: input.printedAt,
		today: input.today,
		grids,
	});
}

function storeLabelOf(input: SchedulesPrintInput, id: number | null): string {
	return input.storeNames.get(id ?? -1) ?? '';
}

/// Une grille d'horaires types, tous libellés résolus (hr-schedule-print reste PUR : il ne sait
/// lire ni un magasin, ni un service, ni une fiche salarié).
function printGridOf(input: SchedulesPrintInput, sch: HrSchedule): SchedulePrintGrid {
	const activity = input.activities.find(a => a.id === hrScheduleActivityId(sch));
	const nameOf = (id: number) => hrEmployeeNameOf(input.employees.find(e => e.id === id), input.users);

	// Les créneaux désignent parfois quelqu'un qui n'est plus membre : ses lignes doivent quand
	// même sortir (hr-schedule-print les remet en fin de grille).
	const ids = Array.from(new Set([...(sch.members ?? []), ...(sch.lnk_slots ?? []).map(s => s.id_employee)]));
	// Le même ordre qu'à l'écran d'édition : celui que le gérant a rangé à la main.
	const order = rosterOrderOf(ids, sch.data, rest => rest
		.slice()
		.sort((a, b) => nameOf(a).localeCompare(nameOf(b))));

	return {
		name: sch.name,
		storeName: storeLabelOf(input, sch.id_store),
		activityName: activity?.name ?? sch.name,
		cycle_weeks: Math.max(1, sch.cycle_weeks),
		anchor_monday: sch.anchor_monday,
		datstart: sch.datstart,
		datend: sch.datend,
		is_active: sch.is_active,
		slots: (sch.lnk_slots ?? []).map(s => ({
			id_employee: s.id_employee,
			week_index: s.week_index,
			weekday: s.weekday,
			start_min: s.start_min,
			end_min: s.end_min,
			break_min: s.break_min ?? 0,
		})),
		members: order.map(id => {
			const emp = input.employees.find(e => e.id === id);
			const contractMin = emp?.contract_hours_week != null ? Math.round(emp.contract_hours_week * 60) : null;
			// Même résolution que le compteur de la semaine : un cadre dirigeant ou un forfait
			// jours n'a pas d'écart au contrat, et le papier ne doit pas lui en inventer un.
			const resolved = effectiveWorkRules(contractMin, input.rules, employeeWorkProfileOf(emp?.data));
			return {
				id,
				name: nameOf(id),
				color: input.employeeColor(id),
				contract_min: contractMin,
				// Modulation : le contrat n'est pas le même à chaque semaine du cycle (34 h / 36 h).
				cycle_contract_min: emp?.cycle_hours?.length ? emp.cycle_hours.map(h => Math.round(h * 60)) : null,
				counts_hours: resolved.counts_hours,
			};
		}),
		openHours: [1, 2, 3, 4, 5, 6, 7].map(wd => input.mergeRanges(hrOpeningOf(activity, wd))
			.map(o => minutesLabel(o.start_min) + ' – ' + minutesLabel(o.end_min))
			.join(', ')),
	};
}
